/**
 * Registro rápido.
 *
 * Es la pantalla que más se va a usar, así que está pensada para escribir una
 * frase y pulsar guardar: «almuerzo 25k», «taxi 12.000», «papitas 3.500». El
 * importe y la categoría se deducen solos.
 *
 * Dos decisiones de comportamiento que importan:
 *
 *  · **Si la clasificación no está segura, se pregunta.** Por debajo del umbral de
 *    confianza aparece la propuesta con la explicación y el usuario decide. Es
 *    mejor preguntar que archivar silenciosamente un gasto en la categoría
 *    equivocada, porque un informe equivocado es peor que una pregunta.
 *  · **Si no se reconoce ningún importe, se dice.** Nunca se guarda un cero
 *    silencioso: guardar un gasto en cero es peor que no guardarlo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Check, Split, Sparkles, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CONFIDENCE_CONFIRMATION_THRESHOLD,
  TRANSACTION_KINDS,
  categoryLabel,
  classifyExpense,
  formatCOP,
  kindLabel,
  parseAmountFromText,
  toLocalTimeHHmm,
  todayISO,
  type TransactionKind,
} from '@eldescuadre/core';

import { ApiError } from '../lib/api';
import { createTransaction, getAccounts, getCategories, getFiadoStores, getDebts } from '../lib/endpoints';
import { enqueue } from '../lib/offline/outbox';
import { useOffline } from '../lib/offline';
import { Card, ErrorNote, Field, Money, Note, SubmitButton } from '../components/ui';
import {
  ItemBreakdown,
  emptyItem,
  filledItems,
  type DraftItem,
} from '../components/ItemBreakdown';

/** ¿El fallo es de conexión, y no del servidor o de los datos? */
function isConnectionError(error: unknown): boolean {
  if (error instanceof ApiError) {
    // Un error del servidor (500) o de validación (400) no se arregla encolando:
    // hay que corregir el dato o esperar a que el servidor se recupere.
    return error.status === 0 || error.status === 502 || error.status === 503;
  }
  return error instanceof TypeError; // `fetch` falla así cuando no hay red.
}

/** Deja el almacén local listo antes de encolar. */
async function ensureOfflineReady(): Promise<void> {
  const state = useOffline.getState();
  if (!state.storageReady) {
    await state.init();
  }
}

/** Tipos que se ofrecen en el registro rápido, en el orden en que se usan. */
const QUICK_KINDS: readonly TransactionKind[] = [
  'EXPENSE',
  'INCOME',
  'FIADO_PURCHASE',
  'FIADO_PAYMENT',
  'DEBT_PAYMENT',
  'TRANSFER',
];

/** Los tipos que exigen cuenta. */
const NEEDS_ACCOUNT: ReadonlySet<TransactionKind> = new Set([
  'EXPENSE',
  'INCOME',
  'TRANSFER',
  'FIADO_PAYMENT',
  'DEBT_PAYMENT',
  'DEBT_INTEREST',
  'ADJUSTMENT',
]);

export function NewEntryPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [kind, setKind] = useState<TransactionKind>('EXPENSE');
  const [amountText, setAmountText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [fiadoStoreId, setFiadoStoreId] = useState('');
  const [debtId, setDebtId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(() => toLocalTimeHHmm(new Date()));
  const [merchant, setMerchant] = useState('');
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState<{ count: number; last: string; queued: boolean } | null>(null);

  /*
   * El desglose en varias categorías.
   *
   * `showBreakdown` empieza en falso a propósito: la mayoría de los gastos son de una
   * sola cosa, y quien registra un café no debe encontrarse un formulario largo por
   * delante. Se abre solo cuando hace falta.
   */
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [items, setItems] = useState<DraftItem[]>([]);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => getCategories(),
  });
  const stores = useQuery({
    queryKey: ['fiado', 'stores'],
    queryFn: getFiadoStores,
    enabled: kind === 'FIADO_PURCHASE' || kind === 'FIADO_PAYMENT',
  });
  const debts = useQuery({
    queryKey: ['debts'],
    queryFn: getDebts,
    enabled: kind === 'DEBT_PAYMENT' || kind === 'DEBT_INTEREST',
  });

  // Lo que se deduce de la frase escrita.
  const parsed = useMemo(() => {
    const fromText = parseAmountFromText(text);
    const amount = fromText ?? parseAmountFromText(amountText) ?? 0;
    const guess = kind === 'EXPENSE' ? classifyExpense(text, { amount: fromText ?? undefined }) : null;
    return { fromText, amount, guess };
  }, [text, amountText, kind]);

  const effectiveAmount = parsed.amount;

  // Categorías que se pueden elegir según el tipo de movimiento.
  const availableCategories = useMemo(() => {
    const all = categories.data?.categories ?? [];
    if (kind === 'INCOME') return all.filter((c) => c.kind === 'INCOME');
    if (kind === 'EXPENSE' || kind === 'FIADO_PURCHASE') {
      return all.filter((c) => c.kind === 'EXPENSE');
    }
    return [];
  }, [categories.data, kind]);

  /** La categoría propuesta por el clasificador, si acertó. */
  const suggestedCategory = useMemo(() => {
    if (!parsed.guess) return null;
    return availableCategories.find((c) => c.code === parsed.guess?.code) ?? null;
  }, [parsed.guess, availableCategories]);

  const chosenCategoryId = categoryId || suggestedCategory?.id || '';

  const needsAccount = NEEDS_ACCOUNT.has(kind);
  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  /** Avisos que impiden guardar, explicados. */
  const problem = useMemo(() => {
    if (effectiveAmount <= 0) {
      return text.trim()
        ? 'No se reconoce ningún importe en lo que escribiste. Escríbelo en el campo de importe.'
        : 'Escribe cuánto fue.';
    }
    if (needsAccount && !chosenAccountId) return 'Elige de qué cuenta salió o entró el dinero.';
    if (kind === 'FIADO_PURCHASE' && !fiadoStoreId) return 'Elige en qué tienda quedaste debiendo.';
    if (kind === 'FIADO_PAYMENT' && !fiadoStoreId) return 'Elige a qué tienda le estás abonando.';
    if (kind === 'DEBT_PAYMENT' && !debtId) return 'Elige a qué deuda le estás abonando.';
    if ((kind === 'EXPENSE' || kind === 'FIADO_PURCHASE') && !chosenCategoryId) {
      return 'Elige una categoría para que el gasto aparezca en los informes.';
    }
    return null;
  }, [effectiveAmount, text, needsAccount, chosenAccountId, kind, fiadoStoreId, debtId, chosenCategoryId]);

  const save = useMutation({
    mutationFn: async () => {
      // La clave de idempotencia se genera aquí y viaja también a la cola local:
      // si el envío falla sin conexión, al reintentar el servidor no cobra dos veces.
      const key = crypto.randomUUID();

      const payload = {
        kind,
        amount: effectiveAmount,
        currency: 'COP' as const,
        date,
        time,
        merchant: merchant.trim() || text.trim() || undefined,
        notes: notes.trim() || undefined,
        accountId: needsAccount ? chosenAccountId : undefined,
        categoryId: chosenCategoryId || undefined,
        fiadoStoreId: fiadoStoreId || undefined,
        debtId: debtId || undefined,
        // El desglose, solo si de verdad hay líneas escritas. Mandar una lista con
        // líneas vacías haría fallar la validación del servidor por un motivo que el
        // usuario no vería en pantalla.
        ...(filledItems(items).length > 0 ? { items: filledItems(items) } : {}),
        idempotencyKey: key,
      };

      try {
        const result = await createTransaction(payload, key);
        return { ...result, queued: false as const };
      } catch (error) {
        // Si el problema es la conexión, el movimiento **no se pierde**: se guarda
        // en el teléfono y se enviará solo cuando vuelva la red.
        if (isConnectionError(error)) {
          await ensureOfflineReady();

          await enqueue('transaction.create', key, {
            ...payload,
            idempotencyKey: key,
          });
          await useOffline.getState().refreshPending();

          return { queued: true as const, transaction: null, idempotent: false };
        }
        throw error;
      }
    },
    onSuccess: async (result) => {
      setSaved({
        count: (saved?.count ?? 0) + 1,
        last: `${kindLabel(kind)} de ${formatCOP(effectiveAmount)}`,
        queued: result.queued,
      });

      // Se limpia lo que cambia entre un movimiento y el siguiente; se conservan
      // la fecha y la cuenta, que suelen ser las mismas.
      setText('');
      setAmountText('');
      setMerchant('');
      setNotes('');
      setCategoryId('');

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['fiado'] }),
        queryClient.invalidateQueries({ queryKey: ['debts'] }),
      ]);
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  if (saved && !save.isPending && save.isSuccess) {
    return (
      <div className="space-y-4 animate-ed-fade-in">
        <Card>
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ed-success/15 text-ed-success">
              <Check size={28} aria-hidden="true" />
            </span>
            <p className="font-display text-lg font-bold">Guardado</p>
            <p className="text-sm text-ed-text-silver">{saved.last}</p>
            {saved.queued ? (
              <p className="max-w-sm text-xs text-ed-gold">
                Sin conexión: quedó guardado en tu teléfono y se enviará solo cuando vuelva la red.
                No hace falta que hagas nada.
              </p>
            ) : (
              <p className="text-xs text-ed-text-muted">
                El saldo se actualizó solo. No hay que ajustar nada a mano.
              </p>
            )}
            {saved.count > 1 && (
              <p className="text-xs text-ed-text-muted">
                Llevas {saved.count} movimientos registrados en esta sesión.
              </p>
            )}
          </div>
        </Card>

        <SubmitButton type="button" onClick={() => setSaved(null)}>
          Registrar otro
        </SubmitButton>
        <SubmitButton type="button" variant="ghost" onClick={() => navigate('/')}>
          Volver al inicio
        </SubmitButton>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 animate-ed-fade-in"
      onSubmit={(event) => {
        event.preventDefault();
        if (problem) return;
        save.mutate();
      }}
    >
      <header>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Registrar</h1>
        <p className="mt-1 text-sm text-ed-text-muted">
          Escribe qué pasó y cuánto. El resto se completa solo.
        </p>
      </header>

      <Card>
        <Field
          label="¿Qué pasó?"
          htmlFor="descripcion"
          hint="Por ejemplo: «almuerzo 25k», «taxi 12.000», «papitas del descanso»."
        >
          <input
            id="descripcion"
            className="ed-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="almuerzo 25k"
            autoComplete="off"
            autoFocus
          />
        </Field>

        {/* La deducción se muestra mientras se escribe, para que se vea el resultado. */}
        {(parsed.fromText !== null || parsed.guess) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {parsed.fromText !== null && (
              <span className="ed-chip border-ed-gold/40 bg-ed-gold/10 text-ed-gold">
                <Wallet size={12} aria-hidden="true" />
                {formatCOP(parsed.fromText)}
              </span>
            )}

            {parsed.guess && (
              <span
                className={`ed-chip ${
                  parsed.guess.confidence >= CONFIDENCE_CONFIRMATION_THRESHOLD
                    ? 'border-ed-cyan/40 bg-ed-cyan/10 text-ed-cyan-light'
                    : 'border-ed-text-muted/40 bg-ed-bg-elevated/60 text-ed-text-silver'
                }`}
              >
                <Sparkles size={12} aria-hidden="true" />
                {categoryLabel(parsed.guess.code)}
                {parsed.guess.confidence < CONFIDENCE_CONFIRMATION_THRESHOLD && ' · ¿seguro?'}
              </span>
            )}
          </div>
        )}

        {/* Si el clasificador no está seguro, se explica y se pide confirmación. */}
        {parsed.guess && parsed.guess.confidence < CONFIDENCE_CONFIRMATION_THRESHOLD && (
          <div className="mt-3">
            <Note>
              {parsed.guess.reason} Puedes cambiarla abajo antes de guardar.
            </Note>
          </div>
        )}
      </Card>

      <Card title="Tipo">
        <div className="flex flex-wrap gap-2">
          {QUICK_KINDS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setKind(option);
                setCategoryId('');
              }}
              aria-pressed={kind === option}
              className={`min-h-[44px] rounded-full px-3.5 text-sm font-medium transition-colors ${
                kind === option
                  ? 'bg-ed-gold-imperial text-ed-bg-abyss'
                  : 'border border-ed-bg-border/50 text-ed-text-silver hover:bg-ed-bg-elevated/50'
              }`}
            >
              {kindLabel(option)}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Importe y categoría">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Importe (pesos)" htmlFor="importe">
            <input
              id="importe"
              className="ed-input ed-money"
              inputMode="numeric"
              value={amountText || (parsed.fromText !== null ? String(parsed.fromText) : '')}
              onChange={(e) => setAmountText(e.target.value)}
              placeholder="25000"
              autoComplete="off"
            />
          </Field>

          <Field label="Fecha" htmlFor="fecha">
            <div className="relative">
              <input
                id="fecha"
                type="date"
                className="ed-input pl-9"
                value={date}
                onChange={(e) => setDate(e.target.value || todayISO())}
              />
              <Calendar
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ed-text-muted"
                aria-hidden="true"
              />
            </div>
          </Field>
        </div>

        {availableCategories.length > 0 && (
          <div className="mt-4">
            <Field label="Categoría" htmlFor="categoria">
              <select
                id="categoria"
                className="ed-input"
                value={chosenCategoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Elige una categoría…</option>
                {availableCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                    {category.id === suggestedCategory?.id ? '  (sugerida)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {needsAccount && (
          <div className="mt-4">
            <Field label="Cuenta" htmlFor="cuenta">
              <select
                id="cuenta"
                className="ed-input"
                value={chosenAccountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">Elige una cuenta…</option>
                {(accounts.data?.accounts ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {formatCOP(account.balance)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {/* El desglose en varias categorías.
            Una compra en la tienda del barrio casi nunca es de una sola cosa: son
            14.000 de mercado, 4.000 de mecato y 7.000 de la recarga, todo apuntado
            como una sola deuda. Sin desglose, el gasto entero cae en una categoría y
            los informes mienten.

            Va aquí, en un bloque que se abre, y no siempre a la vista: la mayoría de
            los gastos son de una sola cosa y no deben cargarse con un formulario
            largo por delante. */}
        {(kind === 'EXPENSE' || kind === 'FIADO_PURCHASE') && (
          <div className="mt-4">
            {!showBreakdown ? (
              <button
                type="button"
                className="ed-btn-ghost w-full text-xs"
                onClick={() => {
                  setShowBreakdown(true);
                  if (items.length === 0) setItems([emptyItem(), emptyItem()]);
                }}
              >
                <Split size={14} aria-hidden="true" />
                Repartir en varias categorías
              </button>
            ) : (
              <div className="rounded-ed border border-ed-bg-border/40 p-3">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">En qué se fue</p>
                    <p className="mt-0.5 text-xs text-ed-text-muted">
                      Reparte el total entre categorías. Cada línea lleva su importe.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-xs text-ed-text-muted hover:text-ed-text-white"
                    onClick={() => {
                      setShowBreakdown(false);
                      setItems([]);
                    }}
                  >
                    Quitar
                  </button>
                </div>

                <ItemBreakdown
                  items={items}
                  onChange={setItems}
                  categories={availableCategories}
                  total={effectiveAmount}
                  onUseSum={(sum) => {
                    // Se pone el total a partir de las líneas, en el mismo campo de
                    // importe que ya existe para no tener dos verdades distintas.
                    setAmountText(String(sum));
                    setText(String(sum));
                  }}
                />
              </div>
            )}
          </div>
        )}

        {(kind === 'FIADO_PURCHASE' || kind === 'FIADO_PAYMENT') && (
          <div className="mt-4">
            <Field
              label="Tienda"
              htmlFor="tienda"
              hint={
                kind === 'FIADO_PURCHASE'
                  ? 'La compra sube lo que debes, pero no saca dinero de la cuenta.'
                  : 'El abono sí saca el dinero, y no cuenta como gasto nuevo.'
              }
            >
              <select
                id="tienda"
                className="ed-input"
                value={fiadoStoreId}
                onChange={(e) => setFiadoStoreId(e.target.value)}
              >
                <option value="">Elige la tienda…</option>
                {(stores.data?.stores ?? []).map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} · debes {formatCOP(store.balance)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {(kind === 'DEBT_PAYMENT' || kind === 'DEBT_INTEREST') && (
          <div className="mt-4">
            <Field
              label="Deuda"
              htmlFor="deuda"
              hint="El capital baja la deuda; los intereses son gasto. La pantalla de deudas permite separarlos."
            >
              <select
                id="deuda"
                className="ed-input"
                value={debtId}
                onChange={(e) => setDebtId(e.target.value)}
              >
                <option value="">Elige la deuda…</option>
                {(debts.data?.debts ?? []).map((debt) => (
                  <option key={debt.id} value={debt.id}>
                    {debt.entity} · {formatCOP(debt.balance)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}
      </Card>

      <Card title="Detalles (opcional)">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Comercio" htmlFor="comercio">
            <input
              id="comercio"
              className="ed-input"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              placeholder="D1, Éxito, la tienda de la esquina…"
              autoComplete="off"
            />
          </Field>

          <Field label="Hora" htmlFor="hora">
            <input
              id="hora"
              type="time"
              className="ed-input"
              value={time}
              onChange={(e) => setTime(e.target.value || '12:00')}
            />
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Notas" htmlFor="notas">
            <textarea
              id="notas"
              className="ed-input min-h-[72px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Para qué fue, con quién, cualquier detalle que quieras recordar."
            />
          </Field>
        </div>
      </Card>

      {problem && <ErrorNote message={problem} />}

      {error && (
        <ErrorNote
          message={error.message}
          hint={
            error.fields.length > 0
              ? error.fields.map((f) => f.message).join(' ')
              : 'Revisa los datos e inténtalo otra vez.'
          }
        />
      )}

      <div className="sticky bottom-20 z-10 space-y-2 bg-ed-bg-primary/90 py-2 backdrop-blur sm:bottom-0">
        <SubmitButton pending={save.isPending} disabled={problem !== null}>
          {effectiveAmount > 0 ? (
            <>
              Guardar <Money amount={effectiveAmount} className="ml-1" />
            </>
          ) : (
            'Guardar'
          )}
        </SubmitButton>
      </div>

      <p className="px-1 pb-2 text-center text-xs text-ed-text-muted">
        Cada movimiento se guarda en el servidor en el momento, y el saldo se recalcula
        solo. Puedes registrar varios seguidos sin volver al inicio.
      </p>
    </form>
  );
}

/** Tipos que se pueden registrar; se exporta para las pruebas y otras pantallas. */
export { TRANSACTION_KINDS, QUICK_KINDS };
